/**
 * MobileNavDrawer
 *
 * Matches the Figma hamburger sidebar design (node 2394-1298):
 *  - Navigation links: Home, Trending, Communities, Saved, Settings
 *  - CREATE section with gradient icon buttons
 *  - VIEW MODE toggle (List / Tile)
 */
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  Typography,
  Divider,
} from "@mui/material";
import {
  Home as HomeIcon,
  Groups as CommunitiesIcon,
  Article as PostIcon,
  GroupAdd as CommunityAddIcon,
  ShowChart as PositionIcon,
} from "@mui/icons-material";
import { useTheme } from "./ThemeContext";
import { AuthContext } from "../context/AuthContext";

const DRAWER_WIDTH = 280;

// ── Shared nav link row ──────────────────────────────────────────────────────
function NavLink({ icon: Icon, label, active, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        height: 48,
        pl: 2,
        borderRadius: "10px",
        cursor: "pointer",
        transition: "background 0.15s ease",
        backgroundColor: active ? "rgba(0,201,80,0.1)" : "transparent",
        border: active ? "1px solid rgba(0,201,80,0.2)" : "1px solid transparent",
        "&:hover": {
          backgroundColor: active ? "rgba(0,201,80,0.12)" : "rgba(255,255,255,0.04)",
        },
      }}
    >
      <Icon
        sx={{
          fontSize: 20,
          color: active ? "#05df72" : "#d1d5dc",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "1rem",
          fontWeight: 500,
          letterSpacing: "-0.3px",
          color: active ? "#05df72" : "#d1d5dc",
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ── Create action button ─────────────────────────────────────────────────────
function CreateButton({ gradient, icon: Icon, title, subtitle, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        height: 64,
        pl: 2,
        borderRadius: "10px",
        backgroundColor: "#1a1f26",
        cursor: "pointer",
        transition: "background 0.15s ease",
        "&:hover": { backgroundColor: "#242930" },
      }}
    >
      {/* Gradient icon box */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          background: gradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18, color: "#fff" }} />
      </Box>

      {/* Text */}
      <Box>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#f3f4f6",
            letterSpacing: "-0.15px",
            lineHeight: 1.43,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#99a1af",
            lineHeight: 1.33,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Section label ────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#6a7282",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
        pl: 2,
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function MobileNavDrawer({ open, onClose }) {
  useTheme();
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(AuthContext);

  const go = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          backgroundColor: "#0f1419",
          borderRight: "1px solid #1e2939",
          overflowX: "hidden",
        },
      }}
    >
      {/* Top spacer for fixed AppBar */}
      <Box sx={{ height: 64, flexShrink: 0 }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          px: 2,
          pt: 2,
          pb: 4,
          overflowY: "auto",
          flex: 1,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* ── Navigation links ──────────────────────────────────── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <NavLink icon={HomeIcon} label="Home" onClick={() => go("/")} />
          <NavLink icon={CommunitiesIcon} label="Communities" onClick={() => go("/communities")} />
        </Box>

        {/* ── Create section ────────────────────────────────────── */}
        {isLoggedIn && (
          <>
            <Divider sx={{ borderColor: "#1e2939" }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <SectionLabel>Create</SectionLabel>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
                <CreateButton
                  gradient="linear-gradient(135deg, #00c950 0%, #009966 100%)"
                  icon={PostIcon}
                  title="Create Post"
                  subtitle="Share your insights"
                  onClick={() => go("/createPost/")}
                />
                <CreateButton
                  gradient="linear-gradient(135deg, #2b7fff 0%, #0092b8 100%)"
                  icon={CommunityAddIcon}
                  title="Create Community"
                  subtitle="Start a new group"
                  onClick={() => go("/createCommunity/")}
                />
                <CreateButton
                  gradient="linear-gradient(135deg, #ad46ff 0%, #e60076 100%)"
                  icon={PositionIcon}
                  title="Create Position"
                  subtitle="Track your trades"
                  onClick={() => go("/createPosition")}
                />
              </Box>
            </Box>
          </>
        )}

      </Box>
    </Drawer>
  );
}
